import { useQuery } from "@tanstack/react-query";
import { UTILITY_KIND, type UtilityKind } from "../../models/Utility";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UtilityConsumptionReportMode,
	UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";
import { getUtilityConsumptionReport } from "../repositories/ConsumptionRepository";
import { getGasConsumptionReport } from "../repositories/GasConsumptionRepository";
import {
	getUtilityConsumptionReportDateError,
	getUtilityConsumptionReportPeriod,
} from "../utils/calendar";

const ALL_METERS_KEY = ["all-meters"] as const;

export interface UseUtilityConsumptionReportInput {
	targetId: number | null;
	utility: UtilityKind;
	externalMeterIds: string[] | null;
	areMetersLoaded: boolean;
	mode: UtilityConsumptionReportMode;
	sectionId: number | null;
	referenceDate: Date;
	sectionEndBoundary: Date;
	range: UtilityConsumptionReportRange;
	sectionRange: UtilityConsumptionReportRange;
}

export const useUtilityConsumptionReport = ({
	targetId,
	utility,
	externalMeterIds,
	areMetersLoaded,
	mode,
	sectionId,
	referenceDate,
	sectionEndBoundary,
	range,
	sectionRange,
}: UseUtilityConsumptionReportInput) => {
	const activeRange =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION ? sectionRange : range;
	const dateError =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD ||
		mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
			? getUtilityConsumptionReportDateError(activeRange)
			: null;
	const canonicalMeterIds =
		externalMeterIds === null
			? null
			: [...new Set(externalMeterIds)].sort((left, right) =>
					left < right ? -1 : left > right ? 1 : 0,
				);
	const meterScopeKey =
		canonicalMeterIds === null
			? ALL_METERS_KEY
			: (["selected-meters", canonicalMeterIds] as const);
	const period =
		dateError === null
			? getUtilityConsumptionReportPeriod(
					mode,
					mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
						? sectionEndBoundary
						: referenceDate,
					activeRange,
				)
			: null;
	const isGas = utility === UTILITY_KIND.GAS;
	const isSectionMode = mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION;
	const enabled =
		(utility === UTILITY_KIND.WATER || isGas) &&
		areMetersLoaded &&
		dateError === null;
	const query = useQuery({
		queryKey: [
			"utility-consumption-report",
			utility,
			targetId,
			meterScopeKey,
			mode,
			...(mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
				? [["section-scope", sectionId] as const]
				: []),
			period?.startUtc.toISOString() ?? null,
			period?.endUtc.toISOString() ?? null,
		],
		queryFn: ({ signal }) => {
			const startUtc = period?.startUtc as Date;
			const endUtc = period?.endUtc as Date;

			if (isGas) {
				return getGasConsumptionReport(
					{
						targetId,
						zoneKeys: canonicalMeterIds,
						mode,
						sectionId: isSectionMode ? sectionId : null,
						startUtc,
						endUtc,
					},
					signal,
				);
			}

			return getUtilityConsumptionReport(
				{
					targetId,
					externalMeterIds: canonicalMeterIds,
					mode,
					sectionId: isSectionMode ? sectionId : null,
					startUtc,
					endUtc,
				},
				signal,
			);
		},
		enabled,
		retry: 1,
	});

	return { ...query, dateError, period };
};
